import { z } from 'zod';
import {
  DEFAULT_CONFIG,
  KALSHI_TAKER_FEES,
  calculateAllocation,
  currentRound,
  heldTradePnl,
  tradeFees,
  playableRecommendation,
  recordStats,
  validContractPrice,
  type FeeModel,
  type MarketRegime,
  type RecordStats,
  type Recommendation,
  type SettledPredictionRecord,
} from '@whaletail/signal-engine';
import type { Btc15PredictorResponse } from './btc15Types';
import {
  applyOfficialResultsTo,
  incomingSupersedes,
  isOfficiallySettled,
  proxyActual,
  type OfficialRoundResult,
  type SettlementSource,
} from './btc15OfficialSettlement';

export const HISTORY_SCHEMA_VERSION = 1;
const STORAGE_KEY = 'whaletail.btc15History.v1';
const MAX_HISTORY = 1200;

export interface Btc15PredictionRecord {
  roundId: string;
  createdMs: number;
  roundStartMs: number;
  roundCloseMs: number;
  roundOpenPrice: number;
  action: 'UP' | 'DOWN';
  recommendation: Recommendation;
  modelProbability: number;
  regime: MarketRegime;
  trendKey: string;
  distanceBucket: string;
  scoreBucket: number;
  rawScore: number;
  marketTicker: string | null;
  contractEntryPrice: number | null;
  contractQuoteSource: 'live_ask' | 'unavailable';
  settled: boolean;
  settledMs: number | null;
  closePrice: number | null;
  actual: 'UP' | 'DOWN' | null;
  won: boolean | null;
  /** Kalshi's target line (`floor_strike`) for this round, when known. */
  targetLine?: number | null;
  /** Where `actual`/`won` came from. Absent on older records, which were proxy-settled. */
  settlementSource?: SettlementSource;
}

export interface Btc15History {
  schemaVersion: number;
  predictions: Btc15PredictionRecord[];
}

function emptyHistory(): Btc15History {
  return { schemaVersion: HISTORY_SCHEMA_VERSION, predictions: [] };
}

function isStorageAvailable(): boolean {
  try {
    return typeof window !== 'undefined' && !!window.localStorage;
  } catch {
    return false;
  }
}

/**
 * Applies any schema migrations needed to bring an older export up to the
 * current `HISTORY_SCHEMA_VERSION`. There is only one schema so far, so
 * this is a no-op placeholder; future versions add a case here rather than
 * rewriting the load/import paths.
 */
function migrateHistory(history: Btc15History): Btc15History {
  if (history.schemaVersion === HISTORY_SCHEMA_VERSION) return history;
  return { ...history, schemaVersion: HISTORY_SCHEMA_VERSION };
}

/** Whether this device has recorded any predictor rounds, i.e. the Predictor has been opened here before. */
export function hasLocalHistory(): boolean {
  if (!isStorageAvailable()) return false;
  try {
    return window.localStorage.getItem(STORAGE_KEY) !== null;
  } catch {
    return false;
  }
}

export function loadHistory(): Btc15History {
  if (!isStorageAvailable()) return emptyHistory();
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return emptyHistory();
    const parsed = historySchema.safeParse(JSON.parse(raw));
    if (!parsed.success) return emptyHistory();
    return migrateHistory(parsed.data);
  } catch {
    return emptyHistory();
  }
}

/** Sorts oldest-first and caps to `MAX_HISTORY`, stamping the current schema version. */
export function trimHistory(history: Btc15History): Btc15History {
  return {
    schemaVersion: HISTORY_SCHEMA_VERSION,
    predictions: [...history.predictions]
      .sort((a, b) => a.createdMs - b.createdMs)
      .slice(-MAX_HISTORY),
  };
}

export function saveHistory(history: Btc15History): void {
  if (!isStorageAvailable()) return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(trimHistory(history)));
  } catch {
    // Storage may be unavailable (private browsing quota); fail silently,
    // matching the rest of the app's localStorage conventions.
  }
}

/**
 * Records one observation per round (matching the Scriptable script's
 * "one observation per fixed 15m round" rule) and attaches a live quote to
 * an already-recorded, still-open round if the quote was unavailable when
 * first recorded. Returns a new history object; does not mutate the input.
 *
 * `recommendation` must be the caller's own `decide()` output for this
 * round, not `response.signal.action` — `Signal.action` is the model's raw
 * directional score and is never `'SKIP'` by construction (the server-side
 * response only ever carries the uncalibrated `Signal`, see
 * btc15Types.ts), while `recommendation` is what actually gates whether
 * `computeStrategyPnl` treats this round as playable. Passing `action` here
 * would silently make every round "playable" regardless of what `decide()`
 * actually said.
 */
export function recordObservation(
  history: Btc15History,
  response: Btc15PredictorResponse,
  recommendation: Recommendation,
): Btc15History {
  const existingIndex = history.predictions.findIndex(
    (p) => p.roundId === response.round.id,
  );
  const quotePrice =
    response.quote.source === 'live_ask' &&
    validContractPrice(response.quote.contractPrice ?? 0)
      ? response.quote.contractPrice
      : null;

  if (existingIndex === -1) {
    const record: Btc15PredictionRecord = {
      roundId: response.round.id,
      createdMs: Date.parse(response.generatedAt),
      roundStartMs: response.signal.roundStartMs,
      roundCloseMs: response.signal.roundCloseMs,
      roundOpenPrice: response.signal.roundOpenPrice,
      action: response.signal.action,
      recommendation,
      modelProbability: response.signal.baseProb,
      regime: response.signal.regime,
      trendKey: response.signal.trendKey,
      distanceBucket: response.signal.distanceBucket,
      scoreBucket: response.signal.scoreBucket,
      rawScore: response.signal.rawScore,
      marketTicker: response.quote.marketTicker,
      contractEntryPrice: quotePrice,
      contractQuoteSource: quotePrice !== null ? 'live_ask' : 'unavailable',
      targetLine: response.quote.targetLine ?? null,
      settled: false,
      settledMs: null,
      closePrice: null,
      actual: null,
      won: null,
    };
    return { ...history, predictions: [...history.predictions, record] };
  }

  const existing = history.predictions[existingIndex];
  if (existing.contractEntryPrice !== null || quotePrice === null) return history;

  const updated: Btc15PredictionRecord = {
    ...existing,
    contractEntryPrice: quotePrice,
    contractQuoteSource: 'live_ask',
    marketTicker: response.quote.marketTicker,
    targetLine: existing.targetLine ?? response.quote.targetLine ?? null,
  };
  const predictions = [...history.predictions];
  predictions[existingIndex] = updated;
  return { ...history, predictions };
}

/**
 * Provisionally settles the round immediately preceding the current one
 * using the Coinbase-close-price proxy the server already computed
 * (`previousRound`), graded against the round's Kalshi target line when it
 * was captured (else its Coinbase open). This is only a placeholder until
 * `applyOfficialResults` replaces it with Kalshi's own published result —
 * the contract settles on the CF Benchmarks RTI, which a Coinbase spot
 * proxy can disagree with on a close round.
 */
export function settlePreviousRound(
  history: Btc15History,
  response: Btc15PredictorResponse,
): Btc15History {
  const previous = response.previousRound;
  if (!previous || previous.closePrice === null) return history;

  const index = history.predictions.findIndex((p) => p.roundId === previous.roundId);
  if (index === -1 || history.predictions[index].settled) return history;

  const record = history.predictions[index];
  const actual = proxyActual(previous.closePrice, record);
  const updated: Btc15PredictionRecord = {
    ...record,
    settled: true,
    settledMs: Date.now(),
    closePrice: previous.closePrice,
    actual,
    won: record.action === actual,
    settlementSource: 'coinbase_proxy',
  };
  const predictions = [...history.predictions];
  predictions[index] = updated;
  return { ...history, predictions };
}

/**
 * Replaces proxy (or missing) settlements with Kalshi's own published
 * outcome for every round that has one. See btc15OfficialSettlement.ts.
 */
export function applyOfficialResults(
  history: Btc15History,
  results: Map<number, OfficialRoundResult>,
  nowMs: number = Date.now(),
): Btc15History {
  const predictions = applyOfficialResultsTo(history.predictions, results, nowMs);
  return predictions === history.predictions ? history : { ...history, predictions };
}

/** Settled, directional (UP/DOWN) records, oldest first — the shape `calibrate()` needs. */
export function toSettledRecords(history: Btc15History): SettledPredictionRecord[] {
  return history.predictions
    .filter(
      (p): p is Btc15PredictionRecord & { won: boolean } => p.settled && p.won !== null,
    )
    .sort((a, b) => a.createdMs - b.createdMs)
    .map((p) => ({
      won: p.won,
      action: p.action,
      regime: p.regime,
      trendKey: p.trendKey,
      distanceBucket: p.distanceBucket,
      scoreBucket: p.scoreBucket,
    }));
}

export interface AccuracyWindows {
  overall: RecordStats;
  windows: Array<{ n: number; stats: RecordStats }>;
}

const ACCURACY_WINDOW_SIZES = [10, 100, 250, 500, 1200];

export function computeAccuracyWindows(history: Btc15History): AccuracyWindows {
  const resolved = toSettledRecords(history);
  return {
    overall: recordStats(resolved, resolved.length),
    windows: ACCURACY_WINDOW_SIZES.map((n) => ({ n, stats: recordStats(resolved, n) })),
  };
}

export interface StrategyPnlResult {
  eligibleSignals: number;
  pricedSignals: number;
  trades: number;
  valueSkips: number;
  wins: number;
  losses: number;
  stake: number;
  /** Net of Kalshi's taker fee on every buy. */
  pnl: number;
  /** Kalshi taker fees paid across every funded trade (already subtracted from pnl). */
  feesPaid: number;
  /** Return per dollar wagered (pnl / stake) — the same dollar gets re-wagered across many rounds, so this is not return on startingPool. */
  roi: number | null;
  /** Return on starting capital: (endingPool - startingPool) / startingPool. What "Pool: $X -> $Y" implies at a glance. */
  poolReturn: number | null;
  startingPool: number;
  endingPool: number;
}

/**
 * Ports `replayPortfolio()`: replays quarter-Kelly sizing chronologically
 * over settled, playable predictions with a real quoted entry price. SKIP
 * recommendations never enter this replay, matching the non-negotiable
 * rule that SKIP contributes no money, profit, or loss. Kalshi's taker fee
 * is charged on every buy (see packages/signal-engine/src/fees.ts).
 */
export function computeStrategyPnl(
  history: Btc15History,
  startingPool: number = DEFAULT_CONFIG.simulatedStartingPool,
  fees: FeeModel = KALSHI_TAKER_FEES,
): StrategyPnlResult {
  const ordered = history.predictions
    .filter(
      (p) =>
        p.settled &&
        (p.action === 'UP' || p.action === 'DOWN') &&
        playableRecommendation(p.recommendation),
    )
    .sort((a, b) => a.createdMs - b.createdMs);

  let bankroll = startingPool;
  let stake = 0;
  let pnl = 0;
  let feesPaid = 0;
  let trades = 0;
  let wins = 0;
  let pricedSignals = 0;

  for (const prediction of ordered) {
    const entry = prediction.contractEntryPrice;
    if (entry === null || !validContractPrice(entry)) continue;
    pricedSignals++;

    const allocation = calculateAllocation(
      prediction.recommendation,
      prediction.modelProbability,
      entry,
      bankroll,
    );
    if (allocation.stake <= 0) continue;

    const tradePnl = heldTradePnl(allocation.stake, entry, Boolean(prediction.won), fees);

    trades++;
    stake += allocation.stake;
    feesPaid += tradeFees(allocation.stake, entry, null, fees);
    pnl += tradePnl;
    bankroll += tradePnl;
    if (prediction.won) wins++;
  }

  return {
    eligibleSignals: ordered.length,
    pricedSignals,
    trades,
    valueSkips: pricedSignals - trades,
    wins,
    losses: trades - wins,
    stake,
    pnl,
    feesPaid,
    roi: stake ? pnl / stake : null,
    poolReturn: startingPool ? (bankroll - startingPool) / startingPool : null,
    startingPool,
    endingPool: bankroll,
  };
}

/**
 * The same quarter-Kelly replay as computeStrategyPnl, but returning the
 * running bankroll after every funded trade instead of only the final
 * figure — for a trend sparkline next to the "$X -> $Y" stat. Deliberately
 * a separate, parallel loop rather than a shared refactor: computeStrategyPnl
 * is money-correctness logic with its own test coverage, and this stays
 * purely additive so it can never change what that function returns.
 * `__tests__` cross-checks this function's last point against
 * computeStrategyPnl's endingPool for the same input as a drift guard.
 */
export function bankrollSeries(
  history: Btc15History,
  startingPool: number = DEFAULT_CONFIG.simulatedStartingPool,
  fees: FeeModel = KALSHI_TAKER_FEES,
): number[] {
  const ordered = history.predictions
    .filter(
      (p) =>
        p.settled &&
        (p.action === 'UP' || p.action === 'DOWN') &&
        playableRecommendation(p.recommendation),
    )
    .sort((a, b) => a.createdMs - b.createdMs);

  let bankroll = startingPool;
  const points = [bankroll];

  for (const prediction of ordered) {
    const entry = prediction.contractEntryPrice;
    if (entry === null || !validContractPrice(entry)) continue;

    const allocation = calculateAllocation(
      prediction.recommendation,
      prediction.modelProbability,
      entry,
      bankroll,
    );
    if (allocation.stake <= 0) continue;

    bankroll += heldTradePnl(allocation.stake, entry, Boolean(prediction.won), fees);
    points.push(bankroll);
  }

  return points;
}

export interface RecentFundedResult {
  roundId: string;
  action: 'UP' | 'DOWN';
  won: boolean;
}

/**
 * Whether a round actually got a real simulated stake — same fixed-pool
 * sizing the live "Simulated buy" tile itself uses
 * (`DEFAULT_CONFIG.simulatedStartingPool`), not the compounding bankroll
 * `computeStrategyPnl` replays, since that's what the tile actually showed
 * at the time. A SKIP recommendation, a missing quote, or an edge too small
 * to clear the minimum stake was never "funded."
 */
function isFundedRecord(record: Btc15PredictionRecord): boolean {
  return (
    calculateAllocation(
      record.recommendation,
      record.modelProbability,
      record.contractEntryPrice,
      DEFAULT_CONFIG.simulatedStartingPool,
    ).stake > 0
  );
}

/**
 * The most recent `n` settled rounds that were funded (see `isFundedRecord`).
 * Most-recent-first. A round that was never funded is skipped rather than
 * counted as a loss.
 */
export function recentFundedResults(
  history: Btc15History,
  n: number,
): RecentFundedResult[] {
  return history.predictions
    .filter(
      (p): p is Btc15PredictionRecord & { won: boolean } => p.settled && p.won !== null,
    )
    .sort((a, b) => b.createdMs - a.createdMs)
    .filter(isFundedRecord)
    .slice(0, n)
    .map((p) => ({ roundId: p.roundId, action: p.action, won: p.won }));
}

export type RoundLookupResult =
  | { status: 'no-data'; roundStartMs: number; roundCloseMs: number }
  | { status: 'unsettled'; roundStartMs: number; roundCloseMs: number }
  | {
      status: 'not-funded';
      roundStartMs: number;
      roundCloseMs: number;
      recommendation: Recommendation;
    }
  | {
      status: 'funded';
      roundStartMs: number;
      roundCloseMs: number;
      action: 'UP' | 'DOWN';
      won: boolean;
      /** False while the outcome is still the provisional Coinbase-proxy grade. */
      official: boolean;
    };

/**
 * Finds whichever 15-minute round was active at `atMs` (any timestamp
 * inside it — the round boundaries are recomputed the same way
 * `currentRound()` derives them live, so a local wall-clock time picked by
 * the caller maps onto the same fixed epoch-aligned blocks recording used)
 * and reports whether it was funded, and its outcome if settled.
 * `roundStartMs`/`roundCloseMs` are always returned so the caller can show
 * the window even when there's no matching history (e.g. before tracking
 * started, or a round still in progress).
 */
export function lookupRoundAt(history: Btc15History, atMs: number): RoundLookupResult {
  const round = currentRound(atMs);
  const record = history.predictions.find((p) => p.roundId === round.id);

  if (!record) {
    return {
      status: 'no-data',
      roundStartMs: round.startMs,
      roundCloseMs: round.closeMs,
    };
  }
  if (!record.settled || record.won === null) {
    return {
      status: 'unsettled',
      roundStartMs: round.startMs,
      roundCloseMs: round.closeMs,
    };
  }
  if (!isFundedRecord(record)) {
    return {
      status: 'not-funded',
      roundStartMs: round.startMs,
      roundCloseMs: round.closeMs,
      recommendation: record.recommendation,
    };
  }
  return {
    status: 'funded',
    roundStartMs: round.startMs,
    roundCloseMs: round.closeMs,
    action: record.action,
    won: record.won,
    official: isOfficiallySettled(record),
  };
}

export interface LivePosition {
  roundId: string;
  action: 'UP' | 'DOWN';
  stake: number;
  entryPrice: number;
  roundCloseMs: number;
}

/**
 * The current round's already-funded position, if there is one — what
 * actually got locked in at entry, not a live recomputation. `recordObservation`
 * freezes `contractEntryPrice` and `modelProbability` the moment it first
 * records a round, so re-deriving the allocation from those frozen fields
 * (rather than reusing the page's own `allocation`, which re-sizes every
 * poll off the *current* signal/price) is what makes this the actual
 * funded stake instead of a constantly-drifting theoretical one.
 */
export function currentPosition(
  history: Btc15History,
  response: Btc15PredictorResponse,
): LivePosition | null {
  const record = history.predictions.find((p) => p.roundId === response.round.id);
  if (!record || record.settled || record.contractEntryPrice === null) return null;

  const allocation = calculateAllocation(
    record.recommendation,
    record.modelProbability,
    record.contractEntryPrice,
    DEFAULT_CONFIG.simulatedStartingPool,
  );
  if (allocation.stake <= 0) return null;

  return {
    roundId: record.roundId,
    action: record.action,
    stake: allocation.stake,
    entryPrice: record.contractEntryPrice,
    roundCloseMs: record.roundCloseMs,
  };
}

const predictionRecordSchema = z.object({
  roundId: z.string(),
  createdMs: z.number(),
  roundStartMs: z.number(),
  roundCloseMs: z.number(),
  roundOpenPrice: z.number(),
  action: z.enum(['UP', 'DOWN']),
  recommendation: z.enum(['UP', 'DOWN', 'SKIP']),
  modelProbability: z.number(),
  regime: z.enum(['TREND', 'CHOP', 'BREAKOUT', 'VOLATILE']),
  trendKey: z.string(),
  distanceBucket: z.string(),
  scoreBucket: z.number(),
  rawScore: z.number(),
  marketTicker: z.string().nullable(),
  contractEntryPrice: z.number().nullable(),
  contractQuoteSource: z.enum(['live_ask', 'unavailable']),
  settled: z.boolean(),
  settledMs: z.number().nullable(),
  closePrice: z.number().nullable(),
  actual: z.enum(['UP', 'DOWN']).nullable(),
  won: z.boolean().nullable(),
  targetLine: z.number().nullable().optional(),
  settlementSource: z.enum(['kalshi_official', 'coinbase_proxy']).optional(),
});

const historySchema = z.object({
  schemaVersion: z.number(),
  predictions: z.array(predictionRecordSchema),
});

export function exportHistory(history: Btc15History): string {
  return JSON.stringify(history, null, 2);
}

export interface ParsedHistoryResult {
  ok: boolean;
  history: Btc15History | null;
  error: string | null;
}

/**
 * Parses and validates a serialized `Btc15History` blob. Used internally to
 * deserialize the server-side history store (see `serverHistory.ts`) — there
 * is no user-facing import path anymore, but the store's content still needs
 * validating against the schema before use (e.g. a corrupted or stale blob).
 */
export function parseHistoryJson(raw: string): ParsedHistoryResult {
  let json: unknown;
  try {
    json = JSON.parse(raw);
  } catch {
    return { ok: false, history: null, error: 'Not valid JSON.' };
  }

  const parsed = historySchema.safeParse(json);
  if (!parsed.success) {
    return {
      ok: false,
      history: null,
      error: parsed.error.issues[0]?.message ?? 'Does not match the expected schema.',
    };
  }

  return { ok: true, history: migrateHistory(parsed.data), error: null };
}

export interface MergeResult {
  merged: Btc15History;
  addedCount: number;
  duplicateCount: number;
  updatedCount: number;
}

/**
 * Merges an imported history into the existing one, deduplicating by round
 * id. Never destructively overwrites a settled record with an unsettled
 * one; a settled incoming record replaces an unsettled existing one, and an
 * officially-settled incoming record replaces a proxy-settled one.
 */
export function mergeHistory(
  existing: Btc15History,
  incoming: Btc15History,
): MergeResult {
  const byRoundId = new Map(existing.predictions.map((p) => [p.roundId, p]));
  let addedCount = 0;
  let duplicateCount = 0;
  let updatedCount = 0;

  for (const incomingRecord of incoming.predictions) {
    const current = byRoundId.get(incomingRecord.roundId);
    if (!current) {
      byRoundId.set(incomingRecord.roundId, incomingRecord);
      addedCount++;
    } else if (incomingSupersedes(current, incomingRecord)) {
      byRoundId.set(incomingRecord.roundId, incomingRecord);
      updatedCount++;
    } else {
      duplicateCount++;
    }
  }

  return {
    merged: {
      schemaVersion: HISTORY_SCHEMA_VERSION,
      predictions: Array.from(byRoundId.values()),
    },
    addedCount,
    duplicateCount,
    updatedCount,
  };
}
